import { AlertTriangle, ShieldAlert, Info, X, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { SecretMatch } from "@/lib/secretScanner";

interface SecurityAlertProps {
  matches: SecretMatch[];
  onDismiss: () => void;
  onViewFile?: (filePath: string, lineNumber: number) => void;
}

/**
 * SecurityAlert - Displays detected secrets and API keys in generated code.
 * Warns users before they deploy potentially leaked credentials.
 */
export function SecurityAlert({
  matches,
  onDismiss,
  onViewFile,
}: SecurityAlertProps) {
  if (matches.length === 0) return null;

  const criticalMatches = matches.filter((m) => m.severity === "critical");
  const warningMatches = matches.filter((m) => m.severity === "warning");

  const hasCritical = criticalMatches.length > 0;

  return (
    <div
      className={`rounded-lg border p-4 mb-4 ${
        hasCritical
          ? "border-red-500/50 bg-red-500/10"
          : "border-yellow-500/50 bg-yellow-500/10"
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          {hasCritical ? (
            <ShieldAlert className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
          ) : (
            <AlertTriangle className="h-5 w-5 text-yellow-500 flex-shrink-0 mt-0.5" />
          )}
          <div className="space-y-2">
            <h4
              className={`font-semibold text-sm ${
                hasCritical ? "text-red-400" : "text-yellow-400"
              }`}
            >
              {hasCritical
                ? `${criticalMatches.length} Secret${criticalMatches.length !== 1 ? "s" : ""} Detected`
                : `${warningMatches.length} Potential Secret${warningMatches.length !== 1 ? "s" : ""} Found`}
            </h4>
            <p className="text-xs text-muted-foreground">
              {hasCritical
                ? "Remove these secrets before deploying. Use environment variables instead."
                : "Review these matches to ensure no sensitive data is exposed."}
            </p>

            {/* Match list */}
            <div className="space-y-1.5 mt-3">
              {matches.slice(0, 5).map((match, i) => (
                <div
                  key={i}
                  className="flex items-center gap-2 text-xs bg-background/50 rounded px-2 py-1.5"
                >
                  {match.severity === "critical" ? (
                    <ShieldAlert className="h-3.5 w-3.5 text-red-500 flex-shrink-0" />
                  ) : match.severity === "warning" ? (
                    <AlertTriangle className="h-3.5 w-3.5 text-yellow-500 flex-shrink-0" />
                  ) : (
                    <Info className="h-3.5 w-3.5 text-blue-500 flex-shrink-0" />
                  )}
                  <span className="font-medium">{match.secretType}:</span>
                  <code className="font-mono text-[10px] bg-muted px-1 rounded">
                    {match.match}
                  </code>
                  <span className="text-muted-foreground">
                    in {match.filePath}:{match.lineNumber}
                  </span>
                  {onViewFile && (
                    <button
                      onClick={() => onViewFile(match.filePath, match.lineNumber)}
                      className="text-primary hover:underline ml-auto"
                    >
                      View
                    </button>
                  )}
                </div>
              ))}
              {matches.length > 5 && (
                <p className="text-xs text-muted-foreground pl-6">
                  +{matches.length - 5} more
                </p>
              )}
            </div>

            {/* Help link */}
            <a
              href="https://docs.usedayone.net/security/environment-variables"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs text-primary hover:underline mt-2"
            >
              Learn about environment variables
              <ExternalLink className="h-3 w-3" />
            </a>
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={onDismiss}
          className="h-6 w-6 p-0"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

export default SecurityAlert;
